import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "../styles/howTo.scss";
import { useDispatch } from "react-redux";
import { initAppBrowser } from "@actions/initApp";
import styled from "@emotion/styled";
import { FiArrowDown } from "react-icons/fi";
import { ClearedStyleRouterLink } from "@modules/Components";
import ExpansionPanel from "@components/ExpansionPanel";

const HeaderMain = styled.div`
  border-radius: 0px 0px 460px 0px;
  background-image: radial-gradient(
      circle at 100% 0,
      rgba(255, 255, 255, 0.14),
      rgba(255, 255, 255, 0) 57%
    ),
    linear-gradient(to bottom, #271763, #271763);
  position: relative;
  text-align: center;

  @media (max-width: 1275px) {
    border-radius: 0px 0px 360px 0px;
  }
  @media (max-width: 1100px) {
    border-radius: 0px 0px 200px 0px;
  }
  @media (max-width: 600px) {
    border-radius: 0px 0px 120px 0px;
  }
  @media (max-width: 400px) {
    border-radius: 0px 0px 90px 0px;
  }
`;

const Header = styled.h1`
  font-size: 72px;
  line-height: 1.25;
  text-align: center;
  color: #fff;
  max-width: 1287px;
  margin: 0 auto;
  padding-top: 100px;
  padding-bottom: 70px;

  @media (max-width: 1500px) {
    font-size: 66px;
  }
  @media (max-width: 1375px) {
    font-size: 60px;
    padding-bottom: 50px;
    max-width: 985px;
  }
  @media (max-width: 1100px) {
    max-width: 95%;
    font-size: 50px;
  }
  @media (max-width: 999px) {
    max-width: 85%;
  }
  @media (max-width: 728px) {
    max-width: 90%;
  }
  @media (max-width: 650px) {
    font-size: 40px;
  }
  @media (max-width: 400px) {
    font-size: 32px;
  }
`;

const HeaderP = styled.div`
  opacity: 0.8;
  font-size: 18px;
  line-height: 2;
  text-align: center;
  color: #fff;
  max-width: 944px;
  margin: 0 auto;
  @media (max-width: 1100px) {
    max-width: 95%;
    font-size: 16px;
  }
  @media (max-width: 999px) {
    max-width: 85%;
  }
  @media (max-width: 728px) {
    max-width: 90%;
  }
  @media (max-width: 400px) {
    font-size: 14px;
  }
`;

const AbsoluteImageLeft = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;

  @media (max-width: 1275px) {
    width: 250px;
    height: auto;
  }
  @media (max-width: 999px) {
    display: none;
  }
`;

const AbsoluteImageRight = styled.img`
  position: absolute;
  top: 0;
  right: 0;

  @media (max-width: 1275px) {
    width: 100px;
    height: auto;
  }
  @media (max-width: 999px) {
    display: none;
  }
`;

const CircleP = styled.div`
  width: 96px;
  height: 96px;
  box-shadow: 30px 30px 50px 0 rgba(31, 32, 33, 0.15);
  background-color: #ffffff;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  text-align: center;
  margin-top: 68px;
  cursor: pointer;
  bottom: -43px;

  @media (max-width: 999px) {
    margin-top: 0;
  }
`;

const Arrow = styled(FiArrowDown)`
  color: #777984;
  font-size: 26px;
  opacity: 0.8;
  position: absolute;
  bottom: 35%;
  right: 35%;
`;

const TopSection = styled.section`
  padding-top: 180px;
  padding-bottom: 100px;
`;
const BlueLine = styled.div`
  height: 3px;
  background-color: #12b2ea;
  width: 36px;
  margin: ${props => props.margin};

  @media (max-width: 1100px) {
    margin: 0 auto;
  }
`;

const ProcessDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 50px 1fr 50px 1fr;
  margin-top: 100px;

  @media (max-width: 1300px) {
    margin-top: 75px;
  }
  @media (max-width: 1150px) {
    grid-template-columns: 1fr;
    width: 80%;
    grid-row-gap: 50px;
    margin-top: 55px;
  }
  @media (max-width: 767px) {
    width: auto;
  }
`;

const ProcessItem = styled.div`
  font-size: 18px;
  line-height: 1.67;
  text-align: center;
  color: #ffffff;
  text-align: center;

  @media (max-width: 1400px) {
    font-size: 17px;
  }
  @media (max-width: 1300px) {
    font-size: 16px;
  }
  @media (max-width: 1150px) {
    font-size: 18px;
    line-height: 1.67;
    max-width: 278px;
    margin: 0 auto;
  }
  @media (max-width: 630px) {
    margin-top: 31px;
  }
`;

const ProcessImageDiv = styled.div`
  width: 96px;
  height: 96px;
  box-shadow: 29px 29px 44px 0 rgba(31, 32, 33, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin: 0 auto;
`;

const ProcessText = styled.div`
  font-size: 16px;
  line-height: 1.88;
  text-align: center;
  color: #777984;
  max-width: 356px;
  margin: 0 auto;
`;

const ProcessTextHeader = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: 0px;
  text-align: center;
  color: #1f2021;
  padding-top: 42px;
  padding-bottom: 16px;
`;

const SquiglyImageDiv = styled.div`
  position: relative;

  @media (max-width: 1150px) {
    display: none;
  }
`;

const SquiglyImage = styled.img`
  position: absolute;
  left: -50px;
  top: 50px;
  @media (max-width: 1400px) {
    left: -37px;
    top: 38px;
    width: 129px;
  }
`;

const SquiglyImageInverted = styled.img`
  position: absolute;
  left: -50px;
  top: 11px;
  -webkit-transform: rotate(180deg);
  -ms-transform: rotate(180deg);
  transform: rotate(173deg);

  @media (max-width: 1400px) {
    left: -37px;
    width: 129px;
  }
`;

const BottomSection = styled.section`
  text-align: center;
  padding-top: 80px;
  padding-bottom: 80px;

  @media (max-width: 1100px) {
    max-width: 700px;
    margin: 0 auto;
    padding-top: 0px;
  }
  @media (max-width: 500px) {
    max-width: 90%;
  }
`;

const BottomTextContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 1500px) {
    padding-top: 100px;
  }
  @media (max-width: 1100px) {
    grid-row: 1/2;
    text-align: center;
    margin: 0 auto;
  }
`;

const BottomHeader = styled.h3`
  font-size: 36px;
  font-weight: 400;
  line-height: 1.44;
  color: #1f2021;
  padding-top: 36px;
  padding-bottom: 50px;

  @media (max-width: 1500px) {
    font-size: 32px;
    line-height: 1.3;
    padding-bottom: 40px;
  }
`;

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  justify-items: center;
  align-items: center;
  gap: 40px 60px;
  max-width: 800px;
  margin: 0 auto 60px;
  padding-right: 40px;

  & > *:nth-child(1) {
    grid-column: 1 / 3;
  }
  & > *:nth-child(2) {
    grid-column: 3 / 5;
  }
  & > *:nth-child(3) {
    grid-column: 5 / 7;
  }
  & > *:nth-child(4) {
    grid-column: 2 / 4;
  }
  & > *:nth-child(5) {
    grid-column: 4 / 6;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    padding-right: 0;
    & > *:nth-child(n) {
      grid-column: auto;
    }
  }
`;

const PartnerLogo = styled.img`
  max-width: 160px;
  height: 50px;
  object-fit: contain;
  filter: grayscale(100%);
  opacity: 0.6;

  @media (max-width: 600px) {
    max-width: 120px;
  }
`;

const TwoColText = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  max-width: 1000px;
  margin: 0 auto;
  text-align: left;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ColText = styled.div`
  font-size: 16px;
  line-height: 1.88;
  color: #777984;
`;

const ColLink = styled.a`
  color: #ff9c1b;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TopHeader = styled.h2`
  font-weight: 400;
  font-size: 48px;
  line-height: 68px;
  letter-spacing: 0px;
  text-align: center;
  color: #1f2021;
  margin-top: 22px;

  @media (max-width: 600px) {
    font-size: 40px;
    line-height: 1.4;
  }
  @media (max-width: 400px) {
    font-size: 32px;
  }
`;

const PressSection = styled.section`
  padding: 80px 0;
  text-align: center;
`;

const PressGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 900px;
  margin: 40px auto 0;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
  }
`;

const PressCard = styled.a`
  display: block;
  padding: 32px;
  border: 1px solid #e8e9eb;
  border-radius: 8px;
  text-decoration: none;
  text-align: left;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
`;

const PressQuote = styled.p`
  font-size: 16px;
  line-height: 1.75;
  color: #1f2021;
  font-style: italic;
  margin-bottom: 16px;
`;

const PressSource = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #ff9c1b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Faqs = styled.section`
  background-image: linear-gradient(
    to bottom,
    rgba(34, 176, 252, 0.081),
    rgba(34, 176, 252, 0)
  );
  position: relative;
`;

const FaqContent = styled.div`
  width: fit-content;
  margin: 0 auto;
  padding-top: 180px;
  padding-bottom: 100px;

  @media (max-width: 950px) {
    width: 85%;
  }
  @media (max-width: 520px) {
    padding-top: 100px;
  }
`;

const FaqHeader = styled.h4`
  font-size: 36px;
  text-align: center;
  color: #1f2021;
  margin-top: 36px;
  margin-bottom: 60px;
`;

const LinkSpan = styled.span`
  cursor: pointer;
  color: #ff9c1b;
`;

const benefitsArray = [
  {
    header: "Cash in hand",
    text: "Got a gift card gathering dust? Trade it in for real cash — check, ACH, PayPal, or an instant virtual Mastercard you can spend anywhere. Most sellers get paid within 1–2 business days.",
    image: "https://cdn.cardcash.com/website/ui/howiw/drop_1.svg"
  },
  {
    header: "Saving money",
    text: "Buy pre-owned gift cards to your favorite retailers at up to 35% off face value. Our regular shoppers save hundreds — sometimes thousands — of dollars a year.",
    image: "https://cdn.cardcash.com/website/ui/howiw/savings-icon.svg"
  },
  {
    header: "Security",
    text: (
      <>
        Every card you buy is backed by our{" "}
        <ColLink
          href="https://www.cardcash.com/guarantee/"
          target="_blank"
          rel="noopener noreferrer"
        >
          45-Day Balance Guarantee
        </ColLink>
        . If a balance isn&apos;t what we said it was, tell us within 45 days
        and we&apos;ll make it right.
      </>
    ),
    image: "https://cdn.cardcash.com/website/ui/howiw/privacy_1.svg"
  }
];

const pressQuotes = [
  {
    quote:
      "Selling through CardCash is your best and most convenient option for selling a gift card online.",
    source: "CNET",
    href: "https://www.cnet.com/personal-finance/how-to-buy-and-sell-gift-cards-legitimately/"
  },
  {
    quote: "CardCash.com was the best gift card exchange site for sellers.",
    source: "Money",
    href: "https://money.com/sell-gift-cards-for-cash/"
  },
  {
    quote: "If you're selling a gift card, try CardCash.com.",
    source: "ABC",
    href: "https://abc13.com/gift-card-how-to-sell-a-selling-an-unwanted-get-cash-for-cards/4968412/"
  },
  {
    quote:
      "CardCash offers up discounts on gift cards, which are otherwise difficult to find.",
    source: "Wired",
    href: "https://www.wired.com/gallery/extremely-last-minute-gift-ideas/"
  }
];

const faqArray = [
  {
    id: 1,
    question: "How do you make sure the gift cards still have a balance?",
    answer: (
      <>
        Honest answer: it&apos;s hard work, and we take it seriously. Every
        seller on CardCash goes through our fraud detection system &mdash;
        identity verification, balance checks, and proprietary screening
        we&apos;ve built and refined for over 16 years (including our own
        AI-powered tool, FraudFix, launched in 2019). In 2025, we upgraded this
        system and actually increased new-seller approvals by over 10% while
        keeping bad cards off the platform &mdash; meaning faster service for
        legitimate sellers, and the same protection for buyers. And we back
        every purchase with our{" "}
        <ColLink
          href="https://www.cardcash.com/guarantee/"
          target="_blank"
          rel="noopener noreferrer"
        >
          45-Day Balance Guarantee
        </ColLink>
        .
      </>
    )
  },
  {
    id: 2,
    question: "How can I protect myself from bad gift cards?",
    answer: (
      <>
        Three simple things: (1) check your balance when the card arrives
        &mdash; don&apos;t wait, (2) use it within 45 days, and (3) if
        something&apos;s off, email us right away. That&apos;s it. The 45-day
        window isn&apos;t a gotcha &mdash; it&apos;s how we stay ahead of the
        edge cases that crop up in any secondary market, and it&apos;s why we
        can process over a billion dollars in cards without passing risk on to
        you.
        <div style={{ marginTop: "22px" }}>
          One more thing worth saying: if someone calls, texts, or emails
          demanding you buy gift cards on CardCash to pay them &mdash;
          that&apos;s a scam. No real business, agency, or boss ever asks for
          payment in gift cards. More on the common scams at{" "}
          <ColLink
            href="https://www.cardcash.com/gift-card-scams/"
            target="_blank"
            rel="noopener noreferrer"
          >
            cardcash.com/gift-card-scams
          </ColLink>
          .
        </div>
      </>
    )
  },
  {
    id: 3,
    question: "What happens if I have questions that aren't listed here?",
    answer: "Email us, call us, or check us out on Reddit — we read everything."
  }
];

const IsCardCashLegit = () => {
  const dispatch = useDispatch();
  const myRef = useRef(null);
  const [oneIsOpenend, changeOneIsOpenend] = useState(false);
  const [idOfOldItem, setIdOfOldItem] = useState();
  const [otherIsClicked, changeIsOtherClicked] = useState("");

  useEffect(() => {
    dispatch(initAppBrowser(["messages"]));
    window.scrollTo(0, 0);
  }, []);

  const handleCircleClick = () => {
    executeScroll();
  };

  const executeScroll = () => scrollToRef(myRef);

  const scrollToRef = ref => window.scrollTo(0, ref.current.offsetTop);

  const handleOtherClicked = id => {
    if (oneIsOpenend && idOfOldItem === id) {
      changeOneIsOpenend(false);
      setIdOfOldItem();
    } else {
      changeOneIsOpenend(true);
    }
    changeIsOtherClicked(id);
  };

  return (
    <div className="mar-t-128">
      <div>
        <Helmet
          title={"Is CardCash Legit? | CardCash"}
          meta={[
            {
              name: "description",
              content:
                "Learn why CardCash is a safe and legitimate gift card marketplace."
            }
          ]}
        />
        <HeaderMain>
          <AbsoluteImageLeft
            src="https://cdn.cardcash.com/home/images/other/element-background-group.svg"
            alt=""
          />
          <AbsoluteImageRight
            src="https://cdn.cardcash.com/website/ui/howiw/top-right-elements.svg"
            alt=""
          />

          <Header>Is CardCash Legit?</Header>
          <HeaderP>
            Well, we think so. But you&apos;re right to ask &mdash; anyone
            selling you something online should have to prove it. So here&apos;s
            the short version: we&apos;ve been operating since 2009, we&apos;re{" "}
            <ColLink
              href="https://www.bbb.org/us/nj/brick/profile/prepaid-credit-cards/cardcashcom-0221-90028712"
              target="_blank"
              rel="noopener noreferrer"
            >
              BBB A+ accredited
            </ColLink>
            , we&apos;re owned by a company listed on NASDAQ (ticker:{" "}
            <ColLink
              href="https://www.nasdaq.com/market-activity/stocks/gift"
              target="_blank"
              rel="noopener noreferrer"
            >
              GIFT
            </ColLink>
            ), and we&apos;ve processed over $1 billion in gift cards. Keep
            scrolling &mdash; the receipts are below.
          </HeaderP>
          <CircleP onClick={handleCircleClick} ref={myRef}>
            <Arrow className="arrow" />
          </CircleP>
        </HeaderMain>

        <main>
          <TopSection>
            <BlueLine margin={"0 auto"} />
            <TopHeader className="main-container-page-width">
              The benefits our consumers enjoy are as follows:
            </TopHeader>
            <ProcessDiv className="main-container-page-width">
              {benefitsArray.map((item, index) => (
                <React.Fragment key={index}>
                  <ProcessItem>
                    <ProcessImageDiv className="process-image-div">
                      <img src={item.image} alt="" />
                    </ProcessImageDiv>
                    <ProcessTextHeader>{item.header}</ProcessTextHeader>
                    <ProcessText>
                      {item.text}
                      {item.link && (
                        <span>
                          <LinkSpan>
                            <ClearedStyleRouterLink to={item.link}>
                              &nbsp;{item.linkText}
                            </ClearedStyleRouterLink>
                          </LinkSpan>
                          {item.afterLinkText}
                        </span>
                      )}
                    </ProcessText>
                  </ProcessItem>
                  {index === 0 ? (
                    <SquiglyImageDiv>
                      <SquiglyImage
                        src="https://cdn.cardcash.com/website/ui/howiw/DottedLines.svg"
                        alt=""
                      />
                    </SquiglyImageDiv>
                  ) : index === 1 ? (
                    <SquiglyImageDiv>
                      <SquiglyImageInverted
                        src="https://cdn.cardcash.com/website/ui/howiw/DottedLines.svg"
                        alt=""
                      />
                    </SquiglyImageDiv>
                  ) : null}
                </React.Fragment>
              ))}
            </ProcessDiv>
          </TopSection>
          <BottomSection>
            <BottomTextContainer>
              <BlueLine margin={"0 auto"} />
              <BottomHeader>We partner with trusted sites</BottomHeader>
            </BottomTextContainer>
            <LogoGrid>
              <PartnerLogo
                src="https://cdn.cardcash.com/website/ui/exchange_lp/logos/mastercard-logo.svg"
                alt="Mastercard"
              />
              <PartnerLogo
                src="https://cdn.cardcash.com/website/ui/exchange_lp/logos/uber-logo-dark.png"
                alt="Uber"
              />
              <PartnerLogo
                src="https://cdn.cardcash.com/website/ui/exchange_lp/logos/cvs-logo.svg"
                alt="cvs"
              />
              <PartnerLogo
                src="https://cdn.cardcash.com/website/ui/exchange_lp/logos/walmart-logo.svg"
                alt="Walmart"
              />
              <PartnerLogo
                src="https://cdn.cardcash.com/website/ui/exchange_lp/logos/amazon-logo-dark.png"
                alt="Amazon"
              />
            </LogoGrid>
            <TwoColText className="main-container-page-width">
              <ColText>
                <p>
                  When you sell us a card, you can take the cash &mdash; or
                  trade it in for a card from a brand you&apos;ll actually use,
                  often at a higher value. Our Amazon partnership (live at{" "}
                  <ColLink
                    href="https://amazon.cardcash.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    amazon.cardcash.com
                  </ColLink>
                  ) lets you swap any unwanted card for an Amazon eGift card in
                  minutes. Through our Mastercard partnership, you can
                  also get paid instantly on a virtual Mastercard accepted
                  worldwide.
                </p>
              </ColText>
              <ColText>
                <p>
                  And we&apos;re not new at this. In 2013,{" "}
                  <ColLink
                    href="https://www.wsj.com/articles/DJFLBO0020131108e9b8j8jun"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Guggenheim Partners
                  </ColLink>{" "}
                  &mdash; a global financial services firm with $200+ billion
                  under management &mdash; led our Series A. We&apos;ve been
                  named to{" "}
                  <ColLink
                    href="https://www.forbes.com/sites/karstenstrauss/2015/01/27/cardcash-inks-wal-mart-deal-to-grab-a-bigger-chunk-of-the-used-gift-card-market/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Forbes&apos;
                  </ColLink>{" "}
                  &ldquo;America&apos;s Most Promising Companies,&rdquo;
                  profiled in{" "}
                  <ColLink
                    href="https://www.inc.com/john-mcdermott/applicant-of-the-week-cardcash.html"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Inc.
                  </ColLink>
                  , and featured in Wired, CNET, Money, ABC, and Fox. Today,
                  we&apos;re owned by Giftify, Inc., a public company traded on{" "}
                  <ColLink
                    href="https://www.nasdaq.com/market-activity/stocks/gift"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    NASDAQ under GIFT
                  </ColLink>{" "}
                  &mdash; with financials filed quarterly for anyone to review.
                </p>
              </ColText>
            </TwoColText>
          </BottomSection>
          <PressSection>
            <BlueLine margin={"0 auto"} />
            <PressGrid className="main-container-page-width">
              {pressQuotes.map((item, index) => (
                <PressCard
                  key={index}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PressQuote>&ldquo;{item.quote}&rdquo;</PressQuote>
                  <PressSource>— {item.source}</PressSource>
                </PressCard>
              ))}
            </PressGrid>
          </PressSection>
          <Faqs>
            <FaqContent>
              <BlueLine margin={"0 auto"} />
              <FaqHeader>Frequently asked questions</FaqHeader>
              {faqArray.map((item) => (
                <ExpansionPanel
                  header={"Frequently asked questions"}
                  key={item.id}
                  item={item}
                  handleOtherClicked={handleOtherClicked}
                  otherIsClicked={otherIsClicked}
                  currentHeader={"Frequently asked questions"}
                  oneIsOpenend={oneIsOpenend}
                  idOfOldItem={idOfOldItem}
                  setIdOfOldItem={setIdOfOldItem}
                />
              ))}
            </FaqContent>
          </Faqs>
        </main>
      </div>
    </div>
  );
};

export default IsCardCashLegit;
